import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { Role } from '../../enums';

export interface IVcfTrustedDevice {
    readonly primaryUserID?: string;
    readonly teeId?: string;
    readonly imei?: string;
    readonly status?: string;
}

export interface IMobileKeyServiceStatus {
    userRole?: Role;
    keyVoucherFreeCount?: number;
}

export interface IVcfMobileKeysStatusResponse {
    readonly data?: {
        readonly serviceStatus?: IMobileKeyServiceStatus;
        readonly trusteddevice?: IVcfTrustedDevice[];
    };
}

export interface IVcfPermission {
    readonly permissionId?: string;
    readonly frontendAlias?: string;
    readonly mkUserUserName?: string;
    readonly mkAdministratorUserName?: string;
    readonly expirationTimestamp?: string;
    readonly state?: string | null;
    readonly installationState?: string | null;
    readonly removalState?: string | null;
}

export interface IVcfMobileKeysListResponse {
    readonly data?: {
        readonly permissionList?: IVcfPermission[];
    };
}

// https://cardata.apps.emea.vwapps.io/documentation#tag/Mobile-Keys
export class VcfDigitalKeyClient {

    private readonly headers: HeadersInit = {
        'User-Id': '__userId__',
    };

    private readonly resourceHostId: string;

    private getGdcType = (isWcar: boolean) => isWcar ? `myvw-wcar-${this.stage}` : `myvw-mbb-${this.stage}`;

    constructor(private readonly authService: IAuthServiceV2, private readonly stage: string) {
        this.resourceHostId = `myvw-vcf-${stage}`
    };

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Mobile-Keys/operation/getMobileKeysStatus
    public async getMobileKeysStatus(vin: string, isWcar: boolean): Promise<IVcfMobileKeysStatusResponse> {
        const response = await this.authService.get(this.resourceHostId,
            `vehicles/${vin}/mobilekeys/status?gdc=${this.getGdcType(isWcar)}`, this.headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return await response.json() as IVcfMobileKeysStatusResponse;
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Mobile-Keys/operation/getMobileKeysList
    public async getMobileKeysList(vin: string, isWcar: boolean): Promise<IVcfMobileKeysListResponse> {
        const response = await this.authService.get(this.resourceHostId,
            `vehicles/${vin}/mobilekeys/keys?gdc=${this.getGdcType(isWcar)}`, this.headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return await response.json() as IVcfMobileKeysListResponse;
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Mobile-Keys/operation/deleteTrustedDevice
    public async deleteTrustedDevice(teeId: string, vin: string, isWcar: boolean): Promise<void> {
        const response = await this.authService.delete(this.resourceHostId,
            `vehicles/${vin}/trusteddevices/${teeId}?gdc=${this.getGdcType(isWcar)}`, undefined, this.headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Mobile-Keys/operation/deleteMobileKeyPermission
    public async deleteMobileKeyPermission(id: string, vin: string, isWcar: boolean): Promise<void> {
        const response = await this.authService.delete(this.resourceHostId,
            `vehicles/${vin}/permissions/${id}?gdc=${this.getGdcType(isWcar)}`, undefined, this.headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Mobile-Keys/operation/putMobileKeyPermission
    public async putMobileKeyPermission(id: string, frontendAlias: string, vin: string, isWcar: boolean): Promise<void> {
        const response = await this.authService.put(this.resourceHostId,
            `vehicles/${vin}/permissions/${id}?gdc=${this.getGdcType(isWcar)}`, JSON.stringify({ frontendAlias }), this.headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
    }
}